import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../assets/AuthContext';

export default function LoginPage() {
  const { login } = useAuth();
  const navigate  = useNavigate();
  const location  = useLocation();
  const from      = location.state?.from || '/';

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error,    setError]    = useState('');
  const [loading,  setLoading]  = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!username.trim() || !password) return;
    setError('');
    setLoading(true);
    try {
      await login(username.trim(), password);
      navigate(from, { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(detail || 'Incorrect username or password');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(145deg, #0f172a 0%, #0a1424 100%)',
      fontFamily: "'Inter', system-ui, sans-serif",
    }}>

      {/* Background grid texture */}
      <div style={{
        position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 0,
        backgroundImage:
          'linear-gradient(rgba(58,138,196,0.04) 1px, transparent 1px),' +
          'linear-gradient(90deg, rgba(58,138,196,0.04) 1px, transparent 1px)',
        backgroundSize: '40px 40px',
      }} />

      <div style={{ position: 'relative', zIndex: 1, width: '100%', maxWidth: 400, padding: '0 20px' }}>

        {/* Logo block */}
        <div style={{ textAlign: 'center', marginBottom: 36 }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: 56, height: 56, borderRadius: 14,
            background: 'linear-gradient(135deg, #2C72AE 0%, #3A8AC4 100%)',
            boxShadow: '0 8px 24px rgba(58,138,196,0.35)',
            marginBottom: 16,
          }}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
              <path d="M3 17L9 11L13 15L21 7" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M17 7H21V11" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div style={{ color: '#f0f4fe', fontWeight: 700, fontSize: 22, letterSpacing: '-0.3px' }}>
            Tsela OAS
          </div>
          <div style={{ color: 'rgba(148,168,200,0.7)', fontSize: 12, marginTop: 4, letterSpacing: '0.04em' }}>
            OBSTACLE ASSESSMENT SYSTEM
          </div>
        </div>

        {/* Card */}
        <form onSubmit={handleSubmit} style={{
          background: 'rgba(15,28,50,0.85)',
          backdropFilter: 'blur(24px)',
          WebkitBackdropFilter: 'blur(24px)',
          border: '1px solid rgba(58,138,196,0.2)',
          borderRadius: 16,
          padding: '36px 32px',
          boxShadow: '0 24px 60px rgba(0,0,0,0.5)',
        }}>
          <div style={{ marginBottom: 24 }}>
            <div style={{ color: '#f0f4fe', fontWeight: 700, fontSize: 18, marginBottom: 4 }}>
              Sign in
            </div>
            <div style={{ color: 'rgba(148,168,200,0.6)', fontSize: 13 }}>
              Enter your credentials to continue
            </div>
          </div>

          {/* Error banner */}
          {error && (
            <div style={{
              background: 'rgba(220,53,69,0.12)',
              border: '1px solid rgba(220,53,69,0.3)',
              borderRadius: 8, padding: '10px 14px',
              color: '#f87171', fontSize: 13, marginBottom: 20,
            }}>
              {error}
            </div>
          )}

          {/* Username */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', color: 'rgba(148,168,200,0.8)', fontSize: 12, fontWeight: 600, marginBottom: 6, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
              Username
            </label>
            <input
              type="text"
              autoComplete="username"
              autoFocus
              value={username}
              onChange={e => setUsername(e.target.value)}
              placeholder="Enter username"
              style={{
                width: '100%', boxSizing: 'border-box',
                background: 'rgba(10,20,36,0.8)',
                border: '1px solid rgba(58,138,196,0.25)',
                borderRadius: 8, padding: '10px 14px',
                color: '#f0f4fe', fontSize: 14,
                outline: 'none', transition: 'border-color 0.2s',
              }}
              onFocus={e => e.target.style.borderColor = 'rgba(58,138,196,0.7)'}
              onBlur={e  => e.target.style.borderColor = 'rgba(58,138,196,0.25)'}
            />
          </div>

          {/* Password */}
          <div style={{ marginBottom: 28 }}>
            <label style={{ display: 'block', color: 'rgba(148,168,200,0.8)', fontSize: 12, fontWeight: 600, marginBottom: 6, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
              Password
            </label>
            <input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="Enter password"
              style={{
                width: '100%', boxSizing: 'border-box',
                background: 'rgba(10,20,36,0.8)',
                border: '1px solid rgba(58,138,196,0.25)',
                borderRadius: 8, padding: '10px 14px',
                color: '#f0f4fe', fontSize: 14,
                outline: 'none', transition: 'border-color 0.2s',
              }}
              onFocus={e => e.target.style.borderColor = 'rgba(58,138,196,0.7)'}
              onBlur={e  => e.target.style.borderColor = 'rgba(58,138,196,0.25)'}
            />
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={loading || !username.trim() || !password}
            style={{
              width: '100%', padding: '11px 0',
              background: loading || !username.trim() || !password
                ? 'rgba(58,138,196,0.3)'
                : 'linear-gradient(135deg, #2C72AE 0%, #3A8AC4 100%)',
              border: 'none', borderRadius: 8,
              color: loading || !username.trim() || !password ? 'rgba(240,244,254,0.4)' : '#fff',
              fontWeight: 700, fontSize: 14, cursor: loading ? 'wait' : 'pointer',
              letterSpacing: '0.02em', transition: 'all 0.2s',
              boxShadow: loading || !username.trim() || !password ? 'none' : '0 4px 16px rgba(58,138,196,0.4)',
            }}
          >
            {loading ? 'Signing in…' : 'Sign in'}
          </button>
        </form>

        {/* Footer */}
        <div style={{ textAlign: 'center', marginTop: 24, color: 'rgba(148,168,200,0.35)', fontSize: 11 }}>
          ICAO Annex 14 · EUR Doc 015 · PANS-OPS · © 2024 Tsela Global NavTech
        </div>
      </div>
    </div>
  );
}
